import EditIcon from '@mui/icons-material/Edit';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ShareIcon from '@mui/icons-material/Share';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ShareModal } from '@/components/Share/ShareModal';
import { EntityType, Hcphco } from '@/core/graphql/generated/types';
import { Permission } from '@/core/store/auth/auth.types';
import { useHasPermission } from '@/core/store/auth/authSelectors';

import { ProfileStatusDcrControl } from '../ProfileStatusDcrControl/ProfileStatusDcrControl';

import { profileActionsMenuStyles as sx } from './ProfileActionsMenu.styles';

type Props = {
	profile: Hcphco;
	entityType: EntityType;
};

type ActiveModal = 'statusChange' | 'share' | null;

export const ProfileActionsMenu = ({ profile, entityType }: Props) => {
	const { t } = useTranslation();
	const dcrEnabled = useHasPermission(Permission.DcrEnabled);
	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
	const [activeModal, setActiveModal] = useState<ActiveModal>(null);

	const isInactive = profile.statusCda_VId === 'iact';

	const handleOpenMenu = (event: MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleCloseMenu = () => {
		setAnchorEl(null);
	};

	const handleSelectStatusChange = () => {
		setActiveModal('statusChange');
		handleCloseMenu();
	};

	const handleSelectShare = () => {
		setActiveModal('share');
		handleCloseMenu();
	};

	const handleCloseModal = () => {
		setActiveModal(null);
	};

	if (!dcrEnabled) {
		return null;
	}

	return (
		<>
			<Button
				variant='outlined'
				color='primary'
				onClick={handleOpenMenu}
				endIcon={<KeyboardArrowDownIcon />}
				sx={sx.trigger}
			>
				{t('More')}
			</Button>

			<Menu
				anchorEl={anchorEl}
				open={!!anchorEl}
				onClose={handleCloseMenu}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
				transformOrigin={{ vertical: 'top', horizontal: 'right' }}
			>
				<MenuItem onClick={handleSelectStatusChange} sx={sx.menuItem}>
					{isInactive ? (
						<EditIcon fontSize='small' />
					) : (
						<HighlightOffIcon fontSize='small' />
					)}
					{isInactive ? t('Reactivate Profile') : t('Inactivate Profile')}
				</MenuItem>

				<MenuItem onClick={handleSelectShare} sx={sx.menuItem}>
					<ShareIcon fontSize='small' />
					{t('Share')}
				</MenuItem>
			</Menu>

			<ProfileStatusDcrControl
				profile={profile}
				entityType={entityType}
				targetStatus={isInactive ? 'act' : 'iact'}
				open={activeModal === 'statusChange'}
				onClose={handleCloseModal}
			/>

			<ShareModal
				open={activeModal === 'share'}
				onClose={handleCloseModal}
				title={t('Share this profile')}
				description={t('Copy and paste the link above to share this profile.')}
			/>
		</>
	);
};
